import React, { useMemo, useState, useEffect } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { FormControlLabel } from '@mui/material'
import { useDispatch } from 'react-redux'
import { initiateUser, updateNewUser, logout } from '../../state/user/userSlice'
import { store } from '../../app/store'
import { useTranslation } from 'react-i18next'
import { uiTokens } from "../../common/uiTokens"
import { useNavigate } from 'react-router-dom'
import { AllergiesMultiSelect } from '../diet/AllergiesMultiselect'

// Define the steps for the Stepper
const steps = ['onboarding.stepper.STEP1', 'onboarding.stepper.STEP2', 'onboarding.stepper.STEP3']

// Define marks for the Activity Level slider
const activityMarks = (t: (key: string) => string) => [
  { value: 1.2, label: t('onboarding.activity_level.SEDENTARY') },
  { value: 1.55, label: t('onboarding.activity_level.MODERATELY_ACTIVE') },
  { value: 1.9, label: t('onboarding.activity_level.VERY_ACTIVE') }
]

// Mapping of language codes to emoji flags
const languageFlagMap: Record<string, string> = {
  'CS': '🇨🇿',
  'SK': '🇸🇰',
  'EN': '🇬🇧'
}

const parseNumberOrEmpty = (value: string): number | '' => {
  const parsed = Number(value)
  return Number.isNaN(parsed) || value.trim() === '' ? '' : parsed
}

const calculateBMI = (weightText: string, heightText: string): number | '' => {
  const weight = parseNumberOrEmpty(weightText)
  const height = parseNumberOrEmpty(heightText)

  if (!weight || !height) {
    return ''
  }

  const heightInMeters = height / 100
  if (!heightInMeters) {
    return ''
  }

  return weight / (heightInMeters * heightInMeters)
}

const formatDateInput = (date: Date): string => date.toISOString().slice(0, 10)

const calculateRecommendedTargetDate = (currentWeightText: string, targetWeightText: string): string => {
  const currentWeight = parseNumberOrEmpty(currentWeightText)
  const targetWeight = parseNumberOrEmpty(targetWeightText)

  if (!currentWeight || !targetWeight) {
    return ''
  }

  const weightDiff = Math.abs(targetWeight - currentWeight)

  if (weightDiff === 0) {
    return formatDateInput(new Date())
  }

  const healthyRatePerWeek = 0.65
  const requiredWeeks = Math.max(1, Math.ceil(weightDiff / healthyRatePerWeek))
  const target = new Date()

  target.setDate(target.getDate() + requiredWeeks * 7)

  return formatDateInput(target)
}

const calculateWeeklyWeightChange = (
  currentWeightText: string,
  targetWeightText: string,
  targetDate: string
): number | null => {
  const currentWeight = parseNumberOrEmpty(currentWeightText)
  const targetWeight = parseNumberOrEmpty(targetWeightText)
  const parsedDate = targetDate ? new Date(targetDate) : null

  if (!currentWeight || !targetWeight || !parsedDate || Number.isNaN(parsedDate.getTime())) {
    return null
  }

  const days = (parsedDate.getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24)

  if (days <= 0) {
    return null
  }

  return Math.abs(targetWeight - currentWeight) / (days / 7)
}


function GoalStep({
  t,
  currentWeight,
  targetWeight,
  height,
  currentChest,
  currentWaist,
  currentHips,
  currentButtocks,
  currentThigh,
  currentCalf,
  targetDate,
  plannedChest,
  plannedWaist,
  plannedHips,
  plannedButtocks,
  plannedThigh,
  plannedCalf,
  isUnhealthyPace,
  recommendedTargetDate,
  onUseRecommendedTargetDate,
  onTargetDateChange,
  setCurrentWeight,
  setTargetWeight,
  setHeight,
  setCurrentChest,
  setCurrentWaist,
  setCurrentHips,
  setCurrentButtocks,
  setCurrentThigh,
  setCurrentCalf,
  setPlannedChest,
  setPlannedWaist,
  setPlannedHips,
  setPlannedButtocks,
  setPlannedThigh,
  setPlannedCalf,
  isTwoColumns
}: {
  t: (key: string) => string
  currentWeight: string
  targetWeight: string
  height: string
  currentChest: string
  currentWaist: string
  currentHips: string
  currentButtocks: string
  currentThigh: string
  currentCalf: string
  targetDate: string
  isUnhealthyPace: boolean
  recommendedTargetDate: string
  onUseRecommendedTargetDate: () => void
  onTargetDateChange: (value: string) => void
  plannedChest: string
  plannedWaist: string
  plannedHips: string
  plannedButtocks: string
  plannedThigh: string
  plannedCalf: string
  setCurrentWeight: React.Dispatch<React.SetStateAction<string>>
  setTargetWeight: React.Dispatch<React.SetStateAction<string>>
  setHeight: React.Dispatch<React.SetStateAction<string>>
  setCurrentChest: React.Dispatch<React.SetStateAction<string>>
  setCurrentWaist: React.Dispatch<React.SetStateAction<string>>
  setCurrentHips: React.Dispatch<React.SetStateAction<string>>
  setCurrentButtocks: React.Dispatch<React.SetStateAction<string>>
  setCurrentThigh: React.Dispatch<React.SetStateAction<string>>
  setCurrentCalf: React.Dispatch<React.SetStateAction<string>>
  setPlannedChest: React.Dispatch<React.SetStateAction<string>>
  setPlannedWaist: React.Dispatch<React.SetStateAction<string>>
  setPlannedHips: React.Dispatch<React.SetStateAction<string>>
  setPlannedButtocks: React.Dispatch<React.SetStateAction<string>>
  setPlannedThigh: React.Dispatch<React.SetStateAction<string>>
  setPlannedCalf: React.Dispatch<React.SetStateAction<string>>
  isTwoColumns: boolean
}) {
  const currentBMI = useMemo(() => calculateBMI(currentWeight, height), [currentWeight, height])
  const targetBMI = useMemo(() => calculateBMI(targetWeight, height), [targetWeight, height])
  const hasWeightDifferenceLimit = useMemo(() => {
    const currentWeightValue = parseNumberOrEmpty(currentWeight)
    const targetWeightValue = parseNumberOrEmpty(targetWeight)

    if (!currentWeightValue || !targetWeightValue) {
      return false
    }

    return Math.abs(currentWeightValue - targetWeightValue) > 10
  }, [currentWeight, targetWeight])

  const isTargetDateInPast = useMemo(() => {
    if (!targetDate) {
      return false
    }

    const parsedDate = new Date(targetDate)

    if (Number.isNaN(parsedDate.getTime())) {
      return false
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return parsedDate.getTime() < today.getTime()
  }, [targetDate])

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        {t('onboarding.stepper.STEP1')}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', color: '#6F675F' }}>
        {t('onboarding.step1_helper')}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'center', color: '#6F675F' }}>
        {t('onboarding.step1_motivation')}
      </Typography>

      <TextField
        required
        label={t('onboarding.current_weight')}
        type="number"
        value={currentWeight}
        onChange={(e) => setCurrentWeight(e.target.value)}
        fullWidth
        error={hasWeightDifferenceLimit}
        helperText={
          hasWeightDifferenceLimit ? t('onboarding.step1_weight_change_limit_exceeded') : undefined
        }
      />
      <TextField
        required
        label={t('onboarding.target_weight')}
        error={hasWeightDifferenceLimit}
        helperText={
          hasWeightDifferenceLimit ? t('onboarding.step1_weight_change_limit_exceeded') : undefined
        }
        type="number"
        value={targetWeight}
        onChange={(e) => setTargetWeight(e.target.value)}
        fullWidth
      />
      <TextField
        required
        label={t('onboarding.target_date')}
        type="date"
        value={targetDate}
        onChange={(e) => onTargetDateChange(e.target.value)}
        error={isTargetDateInPast}
        helperText={
          isTargetDateInPast
            ? t('onboarding.step1_target_date_past_error')
            : undefined
        }
        InputLabelProps={{ shrink: true }}
        fullWidth
      />
      {isUnhealthyPace && targetDate && recommendedTargetDate && (
        <Box sx={{
          padding: 1.5,
          borderRadius: 2,
          border: `1px solid ${uiTokens.colors.warningText}`,
          backgroundColor: uiTokens.colors.warningBg
        }}>
          <Typography variant="body2" sx={{ color: uiTokens.colors.warningText, mb: 1 }}>
            {t('onboarding.unhealthy_pace_warning').replace('{{recommendedDate}}', recommendedTargetDate)}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{ color: uiTokens.colors.warningText, borderColor: uiTokens.colors.warningText }}
            onClick={onUseRecommendedTargetDate}
          >
            {t('onboarding.use_recommended_target_date')}
          </Button>
        </Box>
      )}
      <TextField
        required
        label={t('onboarding.height')}
        type="number"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        fullWidth
      />
      <TextField
        label={t('onboarding.current_bmi')}
        value={currentBMI === '' ? '' : currentBMI.toFixed(1)}
        fullWidth
        InputProps={{ readOnly: true }}
      />
      <TextField
        label={t('onboarding.target_bmi')}
        value={targetBMI === '' ? '' : targetBMI.toFixed(1)}
        fullWidth
        InputProps={{ readOnly: true }}
      />

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Detailní míry těla (volitelné)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ color: '#6F675F', mb: 2 }}>
            Detailní míry těla nejsou povinné. Umožní lepší sledování změn, ale nejsou nutné pro vygenerování nutričního plánu.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: isTwoColumns ? '1fr 1fr' : '1fr'
            }}
          >
            <TextField
              label="Hrudník"
              type="number"
              value={currentChest}
              onChange={(e) => setCurrentChest(e.target.value)}
              fullWidth
            />
            <TextField
              label="Hrudník (cíl)"
              type="number"
              value={plannedChest}
              onChange={(e) => setPlannedChest(e.target.value)}
              fullWidth
            />
            <TextField
              label="Pas"
              type="number"
              value={currentWaist}
              onChange={(e) => setCurrentWaist(e.target.value)}
              fullWidth
            />
            <TextField
              label="Pas (cíl)"
              type="number"
              value={plannedWaist}
              onChange={(e) => setPlannedWaist(e.target.value)}
              fullWidth
            />
            <TextField
              label="Boky"
              type="number"
              value={currentHips}
              onChange={(e) => setCurrentHips(e.target.value)}
              fullWidth
            />
            <TextField
              label="Boky (cíl)"
              type="number"
              value={plannedHips}
              onChange={(e) => setPlannedHips(e.target.value)}
              fullWidth
            />
            <TextField
              label="Zadek"
              type="number"
              value={currentButtocks}
              onChange={(e) => setCurrentButtocks(e.target.value)}
              fullWidth
            />
            <TextField
              label="Zadek (cíl)"
              type="number"
              value={plannedButtocks}
              onChange={(e) => setPlannedButtocks(e.target.value)}
              fullWidth
            />
            <TextField
              label="Stehna"
              type="number"
              value={currentThigh}
              onChange={(e) => setCurrentThigh(e.target.value)}
              fullWidth
            />
            <TextField
              label="Stehna (cíl)"
              type="number"
              value={plannedThigh}
              onChange={(e) => setPlannedThigh(e.target.value)}
              fullWidth
            />
            <TextField
              label="Lýtka"
              type="number"
              value={currentCalf}
              onChange={(e) => setCurrentCalf(e.target.value)}
              fullWidth
            />
            <TextField
              label="Lýtka (cíl)"
              type="number"
              value={plannedCalf}
              onChange={(e) => setPlannedCalf(e.target.value)}
              fullWidth
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

function BasicInfoStep({
  t,
  age,
  gender,
  activityLevel,
  setAge,
  setGender,
  setActivityLevel
}: {
  t: (key: string) => string
  age: string
  gender: string
  activityLevel: number
  setAge: React.Dispatch<React.SetStateAction<string>>
  setGender: React.Dispatch<React.SetStateAction<string>>
  setActivityLevel: React.Dispatch<React.SetStateAction<number>>
}) {
  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        {t('onboarding.stepper.STEP2')}
      </Typography>
      <TextField
        required
        label={t('onboarding.age')}
        type="number"
        value={age}
        onChange={(e) => setAge(e.target.value)}
        fullWidth
      />

      <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
        {t('common.sex.label')}
        <Tooltip
          title={t('onboarding.tooltip.sex')}
          placement="right"
        >
          <HelpOutlineIcon sx={{ ml: 1, verticalAlign: 'middle' }} />
        </Tooltip>
      </Typography>
      <RadioGroup value={gender} onChange={(e) => setGender(e.target.value)} row>
        <FormControlLabel value="MALE" control={<Radio />} label={t('common.sex.MALE')} />
        <FormControlLabel value="FEMALE" control={<Radio />} label={t('common.sex.FEMALE')} />
      </RadioGroup>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ marginTop: 2 }}>
        <Typography variant="subtitle1">{t('onboarding.activity_level.label')}</Typography>
        <Tooltip
          title={
            `${t('onboarding.activity_level.SEDENTARY')}: ${t('onboarding.activity_level.sedentary_descritption')}\n` +
            `${t('onboarding.activity_level.MODERATELY_ACTIVE')}: ${t('onboarding.activity_level.moderately_active_descritption')}\n` +
            `${t('onboarding.activity_level.VERY_ACTIVE')}: ${t('onboarding.activity_level.very_active_descritption')}`
          }
          placement="right"
        >
          <HelpOutlineIcon sx={{ verticalAlign: 'middle' }} />
        </Tooltip>
      </Stack>
      <Slider
        value={activityLevel}
        onChange={(e, val) => setActivityLevel(val as number)}
        step={0.175}
        min={1.2}
        max={1.9}
        marks={activityMarks(t)}
      />
    </Box>
  )
}

// Step 3: Preferences
function PreferencesStep({
  t,
  dietPreference,
  setDietPreference,
  selectedAllergens,
  setSelectedAllergens,
  preferredLanguages,
  setPreferredLanguages,
  availableLanguages
}: {
  t: (key: string) => string
  dietPreference: string
  setDietPreference: React.Dispatch<React.SetStateAction<string>>
  selectedAllergens: string[]
  setSelectedAllergens: React.Dispatch<React.SetStateAction<string[]>>
  preferredLanguages: string[]
  setPreferredLanguages: React.Dispatch<React.SetStateAction<string[]>>
  availableLanguages: string[]
}) {
  const preferences = ['NORMAL', 'VEGETARIAN', 'VEGAN']

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        {t('onboarding.stepper.STEP3')}
      </Typography>
      <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
        {t('onboarding.preferred_languages')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('onboarding.tooltip.preferred_languages')}
      </Typography>
      <FormControl fullWidth>
        <InputLabel id="preferred-languages-label">{t('onboarding.preferred_languages')}</InputLabel>
        <Select
          labelId="preferred-languages-label"
          multiple
          value={preferredLanguages}
          onChange={(e) => setPreferredLanguages(e.target.value as string[])}
          input={<OutlinedInput label={t('onboarding.preferred_languages')} />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={`${languageFlagMap[value] || ''} ${value}`}
                />
              ))}
            </Box>
          )}
        >
          {availableLanguages.map((lang) => (
            <MenuItem key={lang} value={lang}>
              <Box display="flex" alignItems="center" gap={1}>
                <span role="img" aria-label={lang}>
                  {languageFlagMap[lang] || '🏳️'}
                </span>
                {lang}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        {t('onboarding.diet_preferences.label')}
      </Typography>

      {/* Diet Preference Chips */}
      <Stack direction="row" spacing={1} justifyContent="center">
        {preferences.map((pref) => (
          <Chip
            key={pref}
            label={t(`onboarding.diet_preferences.${pref}`)}
            clickable
            color={dietPreference === pref ? 'primary' : 'default'}
            onClick={() => setDietPreference(pref)}
          />
        ))}
      </Stack>

      {/* Allergens Multi-Select */}
      <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
        {t('onboarding.allergens')}
      </Typography>
      <AllergiesMultiSelect
        value={selectedAllergens}
        onChange={setSelectedAllergens}
      />
    </Box>
  )
}

// Main Onboarding Component
export function Onboarding() {
  const dispatch = useDispatch<any>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  // Stepper state
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    dispatch(logout())
  }, [dispatch])

  // Step 1: Goal
  const [currentWeight, setCurrentWeight] = useState('')
  const [targetWeight, setTargetWeight] = useState('')
  const [height, setHeight] = useState('')

  const [currentChest, setCurrentChest] = useState('')
  const [currentWaist, setCurrentWaist] = useState('')
  const [currentHips, setCurrentHips] = useState('')
  const [currentButtocks, setCurrentButtocks] = useState('')
  const [currentThigh, setCurrentThigh] = useState('')
  const [currentCalf, setCurrentCalf] = useState('')
  

  const [plannedChest, setPlannedChest] = useState('')
  const [plannedWaist, setPlannedWaist] = useState('')
  const [plannedHips, setPlannedHips] = useState('')
  const [plannedButtocks, setPlannedButtocks] = useState('')
  const [plannedThigh, setPlannedThigh] = useState('')
  const [plannedCalf, setPlannedCalf] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [isTargetDateManuallySet, setIsTargetDateManuallySet] = useState(false)

  // Step 2: Basic Info
  const [age, setAge] = useState('')
  const [gender, setGender] = useState('MALE')
  const [activityLevel, setActivityLevel] = useState(1.2)
  const availableLanguages = ['CS', 'SK', 'EN']
  const [preferredLanguages, setPreferredLanguages] = useState<string[]>([])

  // Step 3: Preferences
  const [dietPreference, setDietPreference] = useState('NORMAL')
  const [selectedAllergens, setSelectedAllergens] = useState<string[]>([])
  const recommendedTargetDate = useMemo(() => calculateRecommendedTargetDate(currentWeight, targetWeight), [
    currentWeight,
    targetWeight
  ])

  const isUnhealthyPace = useMemo(() => {
    const weeklyWeightChange = calculateWeeklyWeightChange(currentWeight, targetWeight, targetDate)

    const currentWeightValue = parseNumberOrEmpty(currentWeight)
    const targetWeightValue = parseNumberOrEmpty(targetWeight)

    if (!weeklyWeightChange || !currentWeightValue || !targetWeightValue) {
      return false
    }

    if (targetWeightValue >= currentWeightValue) {
      return false
    }

    return weeklyWeightChange > 0.75
  }, [currentWeight, targetWeight, targetDate])

  useEffect(() => {
    if (!isTargetDateManuallySet) {
      setTargetDate(recommendedTargetDate)
    }
  }, [recommendedTargetDate, isTargetDateManuallySet])
  const handleTargetDateChange = (value: string) => {
    setTargetDate(value)
    setIsTargetDateManuallySet(true)
  }

  const handleUseRecommendedTargetDate = () => {
    setTargetDate(recommendedTargetDate)
    setIsTargetDateManuallySet(false)
  }

  // Determine if the "Next" button should be disabled on each step
  const isStepValid = () => {
    const currentWeightValue = parseNumberOrEmpty(currentWeight)
    const targetWeightValue = parseNumberOrEmpty(targetWeight)
    const hasReasonableWeightChange =
      !(currentWeightValue && targetWeightValue && Math.abs(currentWeightValue - targetWeightValue) > 10)
    const parsedTargetDate = targetDate ? new Date(targetDate) : null
    const isTargetDateValid =
      !!targetDate &&
      parsedTargetDate !== null &&
      !Number.isNaN(parsedTargetDate.getTime()) &&
      parsedTargetDate.getTime() >= new Date().setHours(0, 0, 0, 0)
    if (activeStep === 0) {
      // Step 1 required fields
      return (
        currentWeight.trim() !== '' &&
        targetWeight.trim() !== '' &&
        height.trim() !== '' &&
        targetDate.trim() !== '' &&
        isTargetDateValid &&
        hasReasonableWeightChange
      )
    } else if (activeStep === 1) {
      return age.trim() !== '' && gender.trim() !== ''
    }
    return true
  }

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      handleSubmit()
    } else {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleBack = () => setActiveStep((prev) => prev - 1)

  const handleSubmit = async () => {
    try {
      await dispatch(updateNewUser({ name: 'currentWeight', value: currentWeight }))
      await dispatch(updateNewUser({ name: 'plannedWeight', value: targetWeight }))
      await dispatch(updateNewUser({ name: 'currentHeight', value: height }))
      await dispatch(updateNewUser({ name: 'currentChest', value: currentChest }))
      const measurementDate = formatDateInput(new Date())
      await dispatch(updateNewUser({ name: 'measurementDate', value: measurementDate }))
      await dispatch(updateNewUser({ name: 'targetDate', value: targetDate }))
      await dispatch(updateNewUser({ name: 'currentWaist', value: currentWaist }))
      await dispatch(updateNewUser({ name: 'currentHips', value: currentHips }))
      await dispatch(updateNewUser({ name: 'currentButtocks', value: currentButtocks }))
      await dispatch(updateNewUser({ name: 'currentThigh', value: currentThigh }))
      await dispatch(updateNewUser({ name: 'currentCalf', value: currentCalf }))
      await dispatch(updateNewUser({ name: 'plannedChest', value: plannedChest }))
      await dispatch(updateNewUser({ name: 'plannedWaist', value: plannedWaist }))
      await dispatch(updateNewUser({ name: 'plannedHips', value: plannedHips }))
      await dispatch(updateNewUser({ name: 'plannedButtocks', value: plannedButtocks }))
      await dispatch(updateNewUser({ name: 'plannedThigh', value: plannedThigh }))
      await dispatch(updateNewUser({ name: 'plannedCalf', value: plannedCalf }))
      await dispatch(updateNewUser({ name: 'age', value: age }))
      await dispatch(updateNewUser({ name: 'sex', value: gender }))
      await dispatch(updateNewUser({ name: 'physicalActivityLevel', value: activityLevel }))
      await dispatch(updateNewUser({ name: 'preferredLanguages', value: preferredLanguages }))
      await dispatch(updateNewUser({ name: 'dietPreference', value: dietPreference }))
      await dispatch(updateNewUser({ name: 'allergens', value: selectedAllergens }))

      const updatedUser = {
        ...store.getState().user.newUser,
        bodyMeasurements: store.getState().user.newUser.bodyMeasurements?.map((measurement, index) => {
          if (index === 0) {
            return {
              ...measurement,
              date: measurementDate || measurement.date
            }
          }

          if (index === 1) {
            return {
              ...measurement,
              targetDate: targetDate || measurement.targetDate
            }
          }

          return measurement
        })
      }

      const action = await dispatch(initiateUser(updatedUser))
      if (initiateUser.fulfilled.match(action)) {
        navigate('/user/onboarding/dashboard')
      } else {
        alert(t('Failed to complete onboarding. Please try again.'))
      }
    } catch (error) {
      console.error('Onboarding submission error:', error)
      alert(t('An unexpected error occurred. Please try again.'))
    }
  }

  const renderStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <GoalStep
            t={t}
            currentWeight={currentWeight}
            targetWeight={targetWeight}
            height={height}
            currentChest={currentChest}
            currentWaist={currentWaist}
            currentHips={currentHips}
            currentButtocks={currentButtocks}
            currentThigh={currentThigh}
            currentCalf={currentCalf}
            targetDate={targetDate}
            plannedChest={plannedChest}
            plannedWaist={plannedWaist}
            plannedHips={plannedHips}
            plannedButtocks={plannedButtocks}
            plannedThigh={plannedThigh}
            plannedCalf={plannedCalf}
            isUnhealthyPace={isUnhealthyPace}
            recommendedTargetDate={recommendedTargetDate}
            onUseRecommendedTargetDate={handleUseRecommendedTargetDate}
            onTargetDateChange={handleTargetDateChange}
            setCurrentWeight={setCurrentWeight}
            setTargetWeight={setTargetWeight}
            setHeight={setHeight}
            setCurrentChest={setCurrentChest}
            setCurrentWaist={setCurrentWaist}
            setCurrentHips={setCurrentHips}
            setCurrentButtocks={setCurrentButtocks}
            setCurrentThigh={setCurrentThigh}
            setCurrentCalf={setCurrentCalf}
            setPlannedChest={setPlannedChest}
            setPlannedWaist={setPlannedWaist}
            setPlannedHips={setPlannedHips}
            setPlannedButtocks={setPlannedButtocks}
            setPlannedThigh={setPlannedThigh}
            setPlannedCalf={setPlannedCalf}
            isTwoColumns={isDesktop}
          />
        )
      case 1:
        return (
          <BasicInfoStep
            t={t}
            age={age}
            gender={gender}
            activityLevel={activityLevel}
            setAge={setAge}
            setGender={setGender}
            setActivityLevel={setActivityLevel}
          />
        )
      case 2:
        return (
          <PreferencesStep
            t={t}
            dietPreference={dietPreference}
            setDietPreference={setDietPreference}
            selectedAllergens={selectedAllergens}
            setSelectedAllergens={setSelectedAllergens}
            preferredLanguages={preferredLanguages}
            setPreferredLanguages={setPreferredLanguages}
            availableLanguages={availableLanguages}
          />
        )
      default:
        return <Typography>{t('Unknown step')}</Typography>
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#FBF7F2',
        padding: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 600,
          padding: 4,
          borderRadius: 3,
          backgroundColor: '#FFFFFF'
        }}
      >
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: activeStep === index ? 'bold' : 'normal' }}
                >
                  {t(label)}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ marginTop: 4 }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ marginTop: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{ color: '#7A4A2E', borderColor: '#7A4A2E' }}
              >
                {t('common.form.back')}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid()}
              sx={{
                backgroundColor: '#7A4A2E',
                color: '#FBF7F2',
                '&:hover': { backgroundColor: '#5F3923' }
              }}
            >
              {activeStep === steps.length - 1 ? t('common.form.finish') : t('common.form.next')}
            </Button>
          </Box>
          {!isStepValid() && (
            <Typography variant="body2" sx={{ mt: 1, color: '#6F675F', textAlign: 'right' }}>
              {t('onboarding.validation_required')}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
